// Erfassung der Marketing-Parameter der Landing-URL und Ableitung der Plattform,
// ueber die der Nutzer gekommen ist (Google Ads, Facebook, Instagram, ...).
//
// Speicherort: Die Parameter liegen zunaechst NUR im Arbeitsspeicher. Erst wenn
// Marketing-Consent vorliegt, werden sie zusaetzlich im localStorage abgelegt.
// Hintergrund: Das Auslesen von URL-Parametern und Referrer ist einwilligungs-
// frei, das Ablegen auf dem Endgeraet nach § 25 TTDSG dagegen nicht. Die
// Plattform-Zuordnung funktioniert dadurch auch ohne Consent - sie ueberlebt
// dann aber keinen Reload und keine spaetere Rueckkehr des Nutzers.

import { hasMarketingConsent } from "./gtag";

const STORAGE_KEY = "laub_tracking_params";

// Klick-IDs und Kampagnenparameter, die wir mitnehmen. Neue Plattformen lassen
// sich hier ergaenzen, ohne dass an anderer Stelle etwas angepasst werden muss.
const TRACKED = [
  // Google Ads (gbraid/wbraid ersetzen die gclid bei iOS-/App-Traffic)
  "gclid",
  "gbraid",
  "wbraid",
  // Meta: Facebook, Instagram, Messenger, Audience Network
  "fbclid",
  // Microsoft/Bing Ads
  "msclkid",
  // Plattformuebergreifende Kampagnenparameter
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  // Bestandsparameter der Hauptdomain
  "vkclkid",
  "campid",
];

// Reservierter Schluessel im selben Datensatz - kein URL-Parameter.
const QUELLE_KEY = "marketing_quelle";

export const QUELLE_GOOGLE_ADS = "google_ads";
export const QUELLE_FACEBOOK = "facebook";
export const QUELLE_INSTAGRAM = "instagram";
export const QUELLE_META = "meta";
export const QUELLE_BING_ADS = "bing_ads";
export const QUELLE_SONSTIGE = "sonstige";

// utm_source-Werte -> Plattform. Meta liefert mit dem Makro
// {{site_source_name}} die Werte fb | ig | msg | an.
const UTM_SOURCE_QUELLEN = {
  google: QUELLE_GOOGLE_ADS,
  adwords: QUELLE_GOOGLE_ADS,
  googleads: QUELLE_GOOGLE_ADS,
  "google_ads": QUELLE_GOOGLE_ADS,
  fb: QUELLE_FACEBOOK,
  facebook: QUELLE_FACEBOOK,
  "facebook.com": QUELLE_FACEBOOK,
  msg: QUELLE_FACEBOOK,
  messenger: QUELLE_FACEBOOK,
  ig: QUELLE_INSTAGRAM,
  instagram: QUELLE_INSTAGRAM,
  "instagram.com": QUELLE_INSTAGRAM,
  an: QUELLE_META,
  meta: QUELLE_META,
  bing: QUELLE_BING_ADS,
  microsoft: QUELLE_BING_ADS,
};

// Google-Linker-Parameter (_gl) dekodieren, Format: 1*<fingerprint>*<key>*<wert>*...
// Cross-Domain-Links übergeben die gclid nicht als eigenen Parameter, sondern
// base64-kodiert ("." statt "=" als Padding) im _gcl_aw-Wert: GCL.<timestamp>.<gclid>
function gclidFromLinker(gl) {
  if (!gl) return null;
  const parts = gl.split("*");
  const idx = parts.indexOf("_gcl_aw");
  if (idx === -1 || !parts[idx + 1]) return null;
  try {
    const decoded = window.atob(
      parts[idx + 1].replace(/\./g, "=").replace(/-/g, "+").replace(/_/g, "/")
    );
    const match = decoded.match(/^GCL\.\d+\.(.+)$/);
    return match ? match[1] : null;
  } catch (e) {
    return null;
  }
}

function normalize(value) {
  return String(value || "").trim().toLowerCase();
}

// apiService haengt die Werte unkodiert an die URL - deshalb auf unkritische
// Zeichen reduzieren, bevor etwas an den Server geht.
function sanitize(value) {
  return String(value || "")
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 100);
}

function hasTrackedParams(params) {
  return TRACKED.some((key) => params[key]);
}

// Nur als Feinunterscheidung fuer Meta gedacht: die fbclid allein verraet nicht,
// ob der Klick von Facebook oder Instagram kam.
function quelleAusReferrer(referrer) {
  const host = normalize(referrer).replace(/^https?:\/\//, "").split("/")[0];
  if (!host) return null;
  if (/(^|\.)instagram\.com$/.test(host)) return QUELLE_INSTAGRAM;
  if (/(^|\.)(facebook|messenger)\.com$/.test(host)) return QUELLE_FACEBOOK;
  return null;
}

function resolveQuelle(params, referrer) {
  const utmQuelle = UTM_SOURCE_QUELLEN[normalize(params.utm_source)];
  if (utmQuelle) return utmQuelle;
  if (params.gclid || params.gbraid || params.wbraid) return QUELLE_GOOGLE_ADS;
  if (params.fbclid) return quelleAusReferrer(referrer) || QUELLE_META;
  if (params.msclkid) return QUELLE_BING_ADS;
  return hasTrackedParams(params) ? QUELLE_SONSTIGE : "";
}

// Ohne Consent bleibt alles hier - der localStorage wird nicht angefasst.
let memoryParams = {};

function readStored() {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}") || {};
  } catch (e) {
    return {};
  }
}

function writeStored(params) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(params));
  } catch (e) {
    // localStorage nicht verfuegbar (Privatmodus o.ae.) - bleibt fluechtig
  }
}

export function captureTrackingParams() {
  const search = new URLSearchParams(window.location.search);
  const found = {};
  TRACKED.forEach((key) => {
    const value = search.get(key);
    if (value) found[key] = value;
  });
  if (!found.gclid) {
    const gclid = gclidFromLinker(search.get("_gl"));
    if (gclid) found.gclid = gclid;
  }

  const merged = { ...readStored(), ...memoryParams, ...found };

  // Bringt dieser Seitenaufruf eigene Parameter mit, bestimmen sie die Quelle
  // (Last Touch). Sonst bleibt die bereits bekannte Quelle stehen, damit ein
  // Reload ohne Parameter die Zuordnung nicht verliert.
  let quelle = merged[QUELLE_KEY] || "";
  if (hasTrackedParams(found)) {
    quelle = resolveQuelle(found, document.referrer);
  } else if (!quelle) {
    quelle = resolveQuelle(merged, document.referrer);
  }
  if (quelle) merged[QUELLE_KEY] = quelle;

  memoryParams = merged;
  if (hasMarketingConsent() && Object.keys(merged).length > 0) {
    writeStored(merged);
  }
}

// Nach nachtraeglich erteiltem Consent aufrufen, damit die bereits erfassten
// Parameter auch einen Reload ueberleben.
export function persistTrackingParams() {
  if (!hasMarketingConsent()) return false;
  if (Object.keys(memoryParams).length === 0) return false;
  writeStored({ ...readStored(), ...memoryParams });
  return true;
}

export function getTrackingParams() {
  return { ...readStored(), ...memoryParams };
}

// Kam der Nutzer ueber eine Kampagne?
export function hasMarketingParams() {
  return hasTrackedParams(getTrackingParams());
}

// Plattform, ueber die der Nutzer gekommen ist - "" wenn keine Kampagne.
export function getMarketingQuelle() {
  const params = getTrackingParams();
  // Fallback fuer Datensaetze, die noch ohne gespeicherte Quelle abgelegt wurden.
  return params[QUELLE_KEY] || resolveQuelle(params, "");
}

export function getMarketingKampagne() {
  const params = getTrackingParams();
  return sanitize(params.utm_campaign || params.campid || "");
}
